import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DownloadSimple, FileText, IdentificationCard, FileZip } from "@phosphor-icons/react";
import api, { API } from "@/lib/api";
import { RM, MONTHS, initials } from "@/lib/format";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const download = (path, filename) => {
  const token = localStorage.getItem("pf_token");
  fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
    .then((b) => {
      const url = URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    })
    .catch(() => toast.error("Download failed"));
};

export default function Payslips() {
  const [runs, setRuns] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [runId, setRunId] = useState("");
  const [run, setRun] = useState(null);
  const year = new Date().getFullYear();

  useEffect(() => {
    api.get("/payroll/runs").then(({ data }) => {
      setRuns(data);
      const withItems = data.find((r) => r.summary.employee_count > 0) || data[0];
      if (withItems) setRunId(withItems.id);
    });
    api.get("/employees").then(({ data }) => setEmployees(data));
  }, []);

  useEffect(() => {
    if (runId) api.get(`/payroll/runs/${runId}`).then(({ data }) => setRun(data));
  }, [runId]);

  return (
    <Layout title="Payslips & EA Forms" subtitle="Generate and download payroll documents">
      <Tabs defaultValue="payslips">
        <TabsList className="border border-[#E2E5EA] bg-[#FFFFFF]">
          <TabsTrigger value="payslips" data-testid="tab-payslips" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
            <FileText size={16} className="mr-1.5" /> Payslips
          </TabsTrigger>
          <TabsTrigger value="ea" data-testid="tab-ea" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
            <IdentificationCard size={16} className="mr-1.5" /> EA Forms
          </TabsTrigger>
        </TabsList>

        <TabsContent value="payslips" className="mt-5">
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <div className="max-w-xs flex-1">
              <Select value={runId} onValueChange={setRunId}>
                <SelectTrigger data-testid="payslip-run-select" className="border-[#E2E5EA] bg-[#FFFFFF] text-[#0F172A]"><SelectValue placeholder="Select a run" /></SelectTrigger>
                <SelectContent className="border-[#E2E5EA] bg-[#EEF1F5] text-[#0F172A]">
                  {runs.map((r) => <SelectItem key={r.id} value={r.id}>{MONTHS[r.month]} {r.year}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {run && run.items.length > 0 && (
              <Button
                data-testid="payslips-zip-btn"
                onClick={() => download(`/payroll/runs/${runId}/payslips/zip`, `payslips_${run.year}_${String(run.month).padStart(2, "0")}.zip`)}
                className="bg-red-500 font-semibold text-white hover:bg-red-600"
              >
                <FileZip size={16} className="mr-1.5" /> Download All (ZIP)
              </Button>
            )}
          </div>

          {run && run.items.length > 0 ? (
            <div className="overflow-x-auto border border-[#E2E5EA] bg-[#FFFFFF]">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#E2E5EA] text-left text-xs uppercase tracking-wider text-[#6B7280]">
                    <th className="px-4 py-3 font-medium">Employee</th>
                    <th className="px-4 py-3 text-right font-medium">Net Pay</th>
                    <th className="px-4 py-3 text-right font-medium">Payslip</th>
                  </tr>
                </thead>
                <tbody>
                  {run.items.map((it) => (
                    <tr key={it.id} className="border-b border-[#EDEFF2] hover:bg-[#EEF1F5]">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#EEF1F5] text-xs font-semibold text-red-500">{initials(it.employee_name)}</div>
                          <div>
                            <p className="font-medium text-[#0F172A]">{it.employee_name}</p>
                            <p className="text-xs text-[#6B7280]">{it.employee_no}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold tabular-nums text-red-500">{RM(it.net_pay)}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          data-testid={`dl-payslip-${it.employee_no}`}
                          onClick={() => download(`/payroll/runs/${runId}/payslip/${it.employee_id}`, `payslip_${it.employee_no}.pdf`)}
                          className="inline-flex items-center gap-1.5 rounded-md border border-[#E2E5EA] px-3 py-1.5 text-xs text-[#0F172A] transition-colors duration-200 hover:border-emerald-500/40 hover:text-emerald-400"
                        >
                          <DownloadSimple size={14} /> PDF
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="border border-dashed border-[#E2E5EA] bg-[#FFFFFF] py-16 text-center text-sm text-[#9CA3AF]">
              {runId ? "This run has no calculated payslips yet. Calculate the run first." : "Select a payroll run to view payslips."}
            </div>
          )}
        </TabsContent>

        <TabsContent value="ea" className="mt-5">
          <p className="mb-4 text-sm text-[#9CA3AF]">Annual EA form (C.P.8A) for year {year}, aggregated from all processed runs.</p>
          <div className="overflow-x-auto border border-[#E2E5EA] bg-[#FFFFFF]">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E2E5EA] text-left text-xs uppercase tracking-wider text-[#6B7280]">
                  <th className="px-4 py-3 font-medium">Employee</th>
                  <th className="px-4 py-3 font-medium">Tax No</th>
                  <th className="px-4 py-3 text-right font-medium">EA Form {year}</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((e) => (
                  <tr key={e.id} className="border-b border-[#EDEFF2] hover:bg-[#EEF1F5]">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#EEF1F5] text-xs font-semibold text-red-500">{initials(e.full_name)}</div>
                        <div>
                          <p className="font-medium text-[#0F172A]">{e.full_name}</p>
                          <p className="text-xs text-[#6B7280]">{e.employee_no}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 tabular-nums text-[#9CA3AF]">{e.tax_number || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        data-testid={`dl-ea-${e.employee_no}`}
                        onClick={() => download(`/employees/${e.id}/ea-form/${year}`, `EA_${e.employee_no}_${year}.pdf`)}
                        className="inline-flex items-center gap-1.5 rounded-md border border-[#E2E5EA] px-3 py-1.5 text-xs text-[#0F172A] transition-colors duration-200 hover:border-emerald-500/40 hover:text-emerald-400"
                      >
                        <DownloadSimple size={14} /> PDF
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </Layout>
  );
}
